import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Target, Award, TrendingUp, CheckCircle, Lightbulb } from "lucide-react";

const About = () => {
  const teamValues = [
    {
      icon: Target,
      title: "Pipeline-Focused",
      description: "Every strategy is measured by revenue impact and qualified pipeline contribution, not vanity metrics."
    },
    {
      icon: Users,
      title: "Strategic Partnership",
      description: "We embed with your revenue team to align marketing, sales, and growth objectives."
    },
    {
      icon: Award,
      title: "Enterprise Experience",
      description: "Proven track record with B2B companies scaling from £250k to £10M+ in annual revenue."
    },
    {
      icon: Lightbulb,
      title: "Systems-First Thinking",
      description: "We build repeatable demand generation infrastructure that compounds over time."
    }
  ];

  const trustFactors = [
    "Average 3.2x pipeline increase within 90 days",
    "95% client retention rate across enterprise accounts", 
    "£2.4M+ pipeline influenced for clients",
    "Multi-channel ABM and demand generation expertise",
    "Specialists in B2B SaaS, professional services, and technology",
    "Full-funnel attribution and revenue operations alignment"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20" style={{ background: 'var(--gradient-hero)' }}>
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              About <span className="text-secondary">Vincent Growth Partners</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
              We build B2B growth infrastructure for companies serious about revenue. 
              Demand generation, pipeline acceleration, and systems that scale.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                  Our <span className="text-secondary">Story</span>
                </h2>
                <p className="text-xl text-muted-foreground mb-6 leading-relaxed">
                  Vincent Growth Partners was founded to solve a specific problem: B2B companies 
                  with strong products but unpredictable pipeline.
                </p>
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  We saw too many revenue teams relying on hope instead of systems. Sporadic 
                  outbound, inconsistent inbound, and no clear attribution between marketing 
                  spend and closed-won revenue.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Today, we partner with B2B companies in SaaS, professional services, fintech, 
                  and technology to build demand generation infrastructure that delivers 
                  predictable, qualified pipeline month over month.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                {teamValues.map((value, index) => (
                  <Card key={index} className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mx-auto mb-4">
                        <value.icon className="w-6 h-6 text-secondary" />
                      </div>
                      <h3 className="font-bold text-foreground mb-2">{value.title}</h3>
                      <p className="text-sm text-muted-foreground">{value.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Our <span className="text-secondary">Mission</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
              To give B2B companies the same demand generation capabilities as 
              enterprise sales organizations—without the enterprise headcount.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-elegant">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-bold text-secondary mb-2">3.2x</div>
                  <p className="text-muted-foreground">Average Pipeline Increase</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-elegant">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-bold text-secondary mb-2">90 Days</div>
                  <p className="text-muted-foreground">To Qualified Pipeline</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-elegant">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-bold text-secondary mb-2">£2.4M+</div>
                  <p className="text-muted-foreground">Pipeline Influenced</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center text-foreground">
              Why Businesses <span className="text-secondary">Trust VGP</span>
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {trustFactors.map((factor, index) => (
                <div key={index} className="flex items-center">
                  <CheckCircle className="w-6 h-6 text-secondary mr-4 flex-shrink-0" />
                  <span className="text-lg text-muted-foreground">{factor}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-light">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready for Predictable Pipeline?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Let's discuss how VGP can build demand generation infrastructure 
              that delivers qualified opportunities to your sales team.
            </p>
            <Button variant="premium" size="xl" asChild>
              <a href="/do-you-qualify">
                Schedule Strategic Review
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;